import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {ENTITLEMENT_KEYS} from '@packages/domains/subscription-and-payments/entitlements/data-types';
import {notInSiteKinds} from '@packages/domains/site-access/abilities-dimension';

import {defineAbilityConfig} from '../system/defineAbilityConfig';

/**
 * Feature gate for Campaign Build Mode.
 * Checks if the site is an AISB site, has the campaign site kind, and either
 * the feature flag is enabled or the workspace holds the campaign-core
 * entitlement. This can be used independently of mode/permission checks.
 */
export const campaignBuilderAbility = defineAbilityConfig([
  {
    siteKinds: {campaign: true},
    featureFlags: {[FEATURE_FLAGS.CAMPAIGN_BUILD_MODE]: true},
  },
  {
    siteKinds: {campaign: true},
    entitlements: [ENTITLEMENT_KEYS.ALLOW_CAMPAIGN_CORE],
  },
] as const);

/**
 * Shows the Campaign panels. On a read-only link the Designer can't tell who is
 * viewing (for example, a Webflow admin or a signed-out viewer), so the panels
 * show for everyone and the server decides which Campaign data each viewer
 * gets.
 */
export const canReadCampaign = defineAbilityConfig([
  {
    userTypes: {authorizedInDesigner: true, unauthorized: true},
    abilities: {campaignBuilderAbility: true},
    permission: ['read', 'siteCampaign'],
  },
  {
    userTypes: {authorizedInDesigner: true, unauthorized: true},
    abilities: {campaignBuilderAbility: true},
    isReadOnly: true,
  },
] as const);

export const canReviewCampaignContent = defineAbilityConfig([
  {abilities: {canReadCampaign: true}},
  {
    userTypes: {anonymousComment: true},
    abilities: {campaignBuilderAbility: true},
  },
] as const);

export const canManageCampaign = defineAbilityConfig({
  userTypes: {authorizedInDesigner: true},
  abilities: {campaignBuilderAbility: true},
  permission: ['manage', 'siteCampaign'],
} as const);

/**
 * Keeps the Campaign Add panel and its preview available only to users with
 * page-building access; temporary canvas restrictions disable the icon instead.
 */
export const canAccessCampaignAddPanel = defineAbilityConfig({
  abilities: {
    campaignBuilderAbility: true,
    pageBuilderAbility: true,
  },
} as const);

/**
 * Connecting, authorizing, or disconnecting a Campaigns CRM integration calls
 * the Intellimize integration APIs, whose token scope (`manage:integrations`)
 * comes from siteOptimizeIntegrations.manage.
 */
export const canManageCampaignsIntegrations = defineAbilityConfig({
  userTypes: {authorizedInDesigner: true},
  abilities: {
    campaignBuilderAbility: true,
    canManageOptimizationIntegrations: true,
  },
} as const);

/**
 * Editing a brand token in the Brand panel writes a site variable, so it
 * requires the site-variable manage permission on top of the campaign manage
 * ability that opens the panel. Checked as a raw permission rather than
 * through canModifyVariables, whose designer-context requirements would also
 * block editing in brand-panel contexts that are legitimate.
 */
export const canEditBrandPanel = defineAbilityConfig({
  abilities: {canManageCampaign: true},
  permissions: [['manage', 'siteVariable']],
} as const);

/**
 * Shows the Brand panel's Design.md tab to members who can read agent
 * instructions, which is where design.md is stored. On a read-only link the tab
 * shows for everyone and the server decides who gets the document.
 */
export const canReadCampaignDesignMd = defineAbilityConfig([
  {abilities: {canReadAgentInstructions: true}},
  {abilities: {campaignBuilderAbility: true}, isReadOnly: true},
] as const);

export const canLaunchCampaign = defineAbilityConfig({
  abilities: {
    campaignBuilderAbility: true,
    canPublishDesign: true,
  },
} as const);

/**
 * Approving a recommendation writes the suggested text to the page's component
 * map and then records the recommendation as applied, so it needs the page
 * content permission as well as the Campaign one.
 */
export const canApplyCampaignRecommendationToPage = defineAbilityConfig({
  abilities: {
    canManageCampaign: true,
    canEditContent: true,
  },
} as const);

/**
 * The CMS-bound variant of the same approval: the write lands on a collection
 * item's field rather than the component map.
 */
export const canApplyCampaignRecommendationToCmsItem = defineAbilityConfig({
  abilities: {
    canManageCampaign: true,
    canSaveCMSContent: true,
  },
} as const);

/**
 * Shows the Campaign site-mode toggle when Full mode is available in Canvas.
 */
export const canToggleCampaignSiteMode = defineAbilityConfig({
  abilities: {
    campaignBuilderAbility: true,
    canAccessDesignSiteMode: true,
  },
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {
    design: true,
    edit: true,
  },
  workflows: {
    canvas: true,
  },
} as const);

/**
 * Selects the Designer surfaces supported in Full Campaign Mode.
 */
export const isInCampaignFullMode = defineAbilityConfig({
  abilities: {
    campaignBuilderAbility: true,
  },
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {
    design: true,
  },
  workflows: {
    canvas: true,
  },
} as const);

/**
 * Feature gate for the global interface settings rollout.
 */
export const isGlobalInterfaceSettingsEnabled = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.DESIGNER_LIGHT_MODE]: true,
    [FEATURE_FLAGS.GLOBAL_INTERFACE_SETTINGS]: true,
  },
} as const);

/**
 * Determines whether the user is specifically in Campaign Build Mode.
 *
 * Campaign Build Mode is a subset of Page Build Mode — it is true only when
 * isInPageBuildMode is true AND the campaign feature gate is active
 * (AISB site + campaign site kind + feature flag).
 * Use this for campaign-specific UI gating (e.g., campaign panels, campaign-specific controls).
 *
 * For general "am I in any build mode?" checks, use isInPageBuildMode directly.
 */
export const isInCampaignBuildMode = defineAbilityConfig({
  abilities: {
    isInPageBuildMode: true,
    campaignBuilderAbility: true,
  },
} as const);

/**
 * Determines whether Campaign build mode should render the Campaign-owned canvas outline layer.
 */
export const isCampaignCanvasOutlinesEnabled = defineAbilityConfig({
  abilities: {isInCampaignBuildMode: true},
  featureFlags: {[FEATURE_FLAGS.CAMPAIGN_CANVAS_OUTLINES]: true},
} as const);

/**
 * Feature gate for Campaign Build feature flag ONLY.
 * For library creation, we will want our authors to be in design mode on
 * normal sites, so we will only gate by the feature flag enrollment.
 *
 * CAM-374: This is a temporary workaround for the Campaigns project.
 * Remove once native default slot content is supported.
 */
export const isCampaignComponentAuthoringEnabled = defineAbilityConfig({
  featureFlags: {[FEATURE_FLAGS.CAMPAIGNS_COMPONENT_LIBRARY_AUTHORING]: true},
} as const);

/**
 * Determines whether the Navigator panel should be accessible in Campaign Build Mode.
 *
 * This is a feature flag-gated version of campaignBuilderAbility that adds an additional
 * feature flag check (EXPOSE_NAVIGATOR_PANEL_IN_CAMPAIGNS) to control Navigator panel
 * visibility in campaigns.
 */
export const canAccessNavigatorInCampaigns = defineAbilityConfig({
  abilities: {
    campaignBuilderAbility: true,
  },
  featureFlags: {
    [FEATURE_FLAGS.EXPOSE_NAVIGATOR_PANEL_IN_CAMPAIGNS]: true,
  },
} as const);

export const canCreateCampaign = defineAbilityConfig({
  abilities: {canManageCampaign: true},
  permission: ['create', 'sitePage'],
} as const);

export const canManageAdCreative = defineAbilityConfig({
  abilities: {canAccessAdSetCreationPanel: true},
  permission: ['manage', 'adCreative'],
} as const);

export const canUploadAdSetImage = defineAbilityConfig({
  abilities: {canManageAdCreative: true},
  permission: ['manage', 'siteFile'],
} as const);

export const canAccessActivatePanel = defineAbilityConfig({
  abilities: {canReadCampaign: true},
  featureFlags: {[FEATURE_FLAGS.CAMPAIGNS_AD_CREATIVES]: true},
} as const);

/**
 * Determines whether the Ad sets panel (AST-90) is accessible.
 *
 * A feature flag-gated version of campaignBuilderAbility that adds the
 * CAMPAIGNS_AD_CREATIVES check, so the panel isn't reachable by dispatching
 * panelOpened/panelToggled directly regardless of role or flag state.
 *
 * `read` rather than `manage` because the panel is a board view: seeing the
 * creatives is a read, and generating or editing them is authorized per
 * request on the endpoints (AST-219).
 */
export const canAccessAdSetCreationPanel = defineAbilityConfig({
  userTypes: {authorizedInDesigner: true},
  abilities: {
    campaignBuilderAbility: true,
  },
  featureFlags: {
    [FEATURE_FLAGS.CAMPAIGNS_AD_CREATIVES]: true,
  },
  permissions: [['read', 'adCreative']],
} as const);

/**
 * Site Search submits to a Search Results utility page. Campaign hubs publish
 * one Campaign page at a time, so that page is never published and the search
 * form leads to a 404 (CAM-1958). This gates the Search element and its
 * element settings.
 */
export const canAccessSiteSearch = defineAbilityConfig({
  siteKinds: notInSiteKinds('campaign'),
} as const);

/**
 * Gates the Search settings panel. It keeps the panel's previous
 * canAccessProjectSettings rule and adds the Campaign hub exclusion.
 */
export const canAccessSiteSearchSettings = defineAbilityConfig({
  abilities: {canAccessProjectSettings: true, canAccessSiteSearch: true},
} as const);
